import React, { useCallback, useEffect, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { muiTheme } from "@/styles/theme";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { CircularProgress, Typography } from "@mui/material";
import { FilterType } from "@/modules/common/constants";

const StyledDropdown = styled(Dropdown)(({ screen }) => ({
  "& .dropdown-toggle": {
    backgroundColor: "transparent",
    height: "auto",
    minHeight: "40px",
    width: screen.isTablet || screen.isMobile ? "90vw" : "326px",
    justifyContent: "space-between",
    alignItems: "center",
    textAlign: "left",
    display: "inline-flex",
    border: `1px solid ${muiTheme.colorSchemes.light.palette.neutral[200]}`,
    borderRadius: "4px",
    outline: "none",
    color: muiTheme.colorSchemes.light.palette.neutral[500],
    padding: "6px 12px",
    flexWrap: "wrap",
    gap: "4px",
  },
  "& .dropdown-menu": {
    border: `1px solid ${muiTheme.colorSchemes.light.palette.background.blue1}`,
    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.175)",
    minWidth: "100%",
    whiteSpace: "nowrap",
  },
  "& .dropdown-item": {
    textAlign: "left",
    textTransform: "capitalize",
    "&:hover, &:focus": {
      backgroundColor: "#f8f9fa",
    },
    "&.selected": {
      backgroundColor: muiTheme.colorSchemes.light.palette.neutral[100],
    },
  },
  "&.active": {
    backgroundColor: "#d3d3d3",
    color: muiTheme.colorSchemes.light.palette.main.black,
  },
}));

// Add AccountManager type
interface AccountManager {
  uid: string;
  name: string;
}

interface AccountManagersDropdownProps {
  filterType: FilterType;
  setFilterType: React.Dispatch<React.SetStateAction<FilterType>>;
  isBecomeFranchiseOnly: boolean;
}

const AccountManagersDropdown: React.FC<AccountManagersDropdownProps> = ({
  filterType,
  setFilterType,
  isBecomeFranchiseOnly,
}) => {
  const screen = useScreenSize();
  const {
    accountManagers,
    accountManagersLoading,
    accountManagersError,
    selectedManagers,
    setSelectedManagers,
  } = useOrganization();

  useEffect(() => {
    const storedManagers = localStorage.getItem("selectedAccountManagers");
    if (storedManagers) {
      setSelectedManagers(JSON.parse(storedManagers));
    }
  }, []);

  useEffect(() => {
    // Clear selected managers when isBecomeFranchiseOnly becomes true
    if (isBecomeFranchiseOnly) {
      setSelectedManagers([]);
      localStorage.removeItem("selectedAccountManagers");
      setFilterType(FilterType.NONE);
    }
  }, [isBecomeFranchiseOnly, setFilterType]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (eventKey: string | null) => {
    if (
      !eventKey ||
      filterType === FilterType.ORGANIZATION ||
      isBecomeFranchiseOnly
    )
      return;

    const selectedManager = accountManagers?.data?.results?.find(
      (manager: AccountManager) => manager?.uid === eventKey
    );

    if (!selectedManager) return;

    const isAlreadySelected = selectedManagers.some(
      (manager) => manager.uid === selectedManager.uid
    );

    const updatedSelection = isAlreadySelected
      ? selectedManagers.filter(
          (manager) => manager.uid !== selectedManager.uid
        )
      : [...selectedManagers, selectedManager];

    setSelectedManagers(updatedSelection);

    localStorage.setItem(
      "selectedAccountManagers",
      JSON.stringify(updatedSelection)
    );

    setFilterType(FilterType.ACCOUNT_MANAGER);
  };

  const handleDelete = (managerUid: string) => {
    const updatedSelection = selectedManagers.filter(
      (manager) => manager.uid !== managerUid
    );

    if (selectedManagers?.length === 1) {
      setFilterType(FilterType.NONE);
    }

    setSelectedManagers(updatedSelection);
    localStorage.setItem(
      "selectedAccountManagers",
      JSON.stringify(updatedSelection)
    );
  };

  const renderToggleContent = () => {
    if (selectedManagers.length > 0) {
      return (
        <div className="d-flex flex-wrap gap-1">
          {selectedManagers.map((manager) => (
            <Chip
              key={manager?.uid}
              label={manager?.name}
              onDelete={() => handleDelete(manager?.uid)}
              size="small"
              sx={{
                backgroundColor:
                  muiTheme.colorSchemes.light.palette.primary[200],
              }}
            />
          ))}
        </div>
      );
    }
    return "Select Account Manager";
  };

  const getDropDownContent = useCallback(() => {
    if (accountManagersLoading)
      return (
        <Dropdown.Item>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading...</Typography>
        </Dropdown.Item>
      );

    if (accountManagersError)
      return (
        <Dropdown.Item>
          <Typography variant="body2">
            Error loading account managers
          </Typography>
        </Dropdown.Item>
      );

    if (accountManagers?.data?.results?.length === 0)
      return (
        <Dropdown.Item>
          <Typography variant="body2">No account managers found</Typography>
        </Dropdown.Item>
      );

    return accountManagers?.data?.results?.map((manager: AccountManager) => (
      <Dropdown.Item
        key={manager?.uid}
        eventKey={manager?.uid}
        style={{
          textTransform: "capitalize",
          ...(filterType === FilterType.ORGANIZATION || isBecomeFranchiseOnly
            ? {
                cursor: "not-allowed",
              }
            : {}),
        }}
      >
        {manager?.name}
      </Dropdown.Item>
    ));
  }, [
    filterType,
    isBecomeFranchiseOnly,
    accountManagersLoading,
    accountManagersError,
    accountManagers?.data?.results,
  ]);

  //   const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
  //     const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
  //     if (
  //       scrollTop + clientHeight >= scrollHeight - 10 &&
  //       !isFetchingNextAccountManagers
  //     ) {
  //       setAccountManagersPageSize((prev: number) => prev + 10);
  //     }
  //   };

  return (
    <StyledDropdown
      onSelect={handleSelect}
      screen={screen}
      disabled={isBecomeFranchiseOnly || selectedManagers.length > 0}
    >
      <Dropdown.Toggle
        variant="outline-secondary"
        id="account-managers-dropdown"
        disabled={isBecomeFranchiseOnly || selectedManagers.length > 0}
      >
        {renderToggleContent()}
      </Dropdown.Toggle>
      <Dropdown.Menu ref={dropdownRef}>{getDropDownContent()}</Dropdown.Menu>
      {/* 
      {isFetchingNextAccountManagers && (
        <Dropdown.Item>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading more...</Typography>
        </Dropdown.Item>
      )} */}
    </StyledDropdown>
  );
};

export default AccountManagersDropdown;
